import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { styled } from '@mui/material/styles';
import brandingConfig from '../../config/brandingConfig';

const PolicyContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: '900px',
  margin: '0 auto',
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
  borderRadius: theme.shape.borderRadius * 2,
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(1),
}));

const TableStyled = styled(Table)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  '& th, & td': {
    borderColor: theme.palette.divider,
  },
}));

const MobilePrivacyPolicy = () => {
  const currentYear = new Date().getFullYear();
  const currentDate = new Date().toLocaleDateString('de-DE', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });

  return (
    <Container>
      <PolicyContainer elevation={2}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Datenschutzerklärung für die mobile Applikation „{brandingConfig.appName}"
        </Typography>
        <Typography variant="subtitle1" align="center" color="text.secondary" gutterBottom>
          (Fahrgast- & Fahrer-App)
        </Typography>
        <Typography variant="subtitle2" align="center" gutterBottom>
          Stand: {currentDate}
        </Typography>

        <Divider sx={{ my: 3 }} />

        <SectionTitle variant="h5" component="h2">
          1. Verantwortlicher im Sinne der DSGVO
        </SectionTitle>
        <Typography variant="body1" paragraph>
          {brandingConfig.owner && <>{brandingConfig.owner}<br /></>}
          {brandingConfig.companyName}<br />
          {brandingConfig.address}<br />
          Telefon: {brandingConfig.phone}<br />
          E-Mail: {brandingConfig.email}
        </Typography>

        <SectionTitle variant="h5" component="h2">
          2. (Optionaler) Datenschutzbeauftragter
        </SectionTitle>
        <Typography variant="body1" paragraph>
          Nach derzeitiger Mitarbeiteranzahl besteht keine gesetzliche Verpflichtung zur Bestellung eines Datenschutzbeauftragten (vgl. § 38 BDSG). Bei Datenschutzanfragen wenden Sie sich bitte an die oben genannten Kontaktdaten.
        </Typography>

        <SectionTitle variant="h5" component="h2">
          3. Zwecke & Rechtsgrundlagen der Datenverarbeitung
        </SectionTitle>
        <TableContainer component={Paper} elevation={0} variant="outlined">
          <TableStyled size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Zweck</strong></TableCell>
                <TableCell><strong>Betroffene Personen</strong></TableCell>
                <TableCell><strong>Rechtsgrundlage</strong></TableCell>
                <TableCell><strong>Erläuterung</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Vermittlung & Durchführung von Taxifahrten</TableCell>
                <TableCell>Fahrgäste, Fahrer</TableCell>
                <TableCell>Art. 6 Abs. 1 b DSGVO (Vertragserfüllung)</TableCell>
                <TableCell>Verarbeitung von Profil-, Buchungs- und Standortdaten zur Bereitstellung der Kernfunktionen (Sofort- & Vorbestellungen, Disposition, Navigation).</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Echtzeit-Standortverfolgung</TableCell>
                <TableCell>Fahrgäste (Abholort), Fahrer (Position des Fahrzeugs)</TableCell>
                <TableCell>Art. 6 Abs. 1 a DSGVO (Einwilligung)</TableCell>
                <TableCell>Standortfreigabe über OS-Berechtigungsdialog; Widerruf jederzeit über Geräte-Einstellungen.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Push-Benachrichtigungen</TableCell>
                <TableCell>Fahrgäste, Fahrer</TableCell>
                <TableCell>Art. 6 Abs. 1 a DSGVO (Einwilligung)</TableCell>
                <TableCell>Updates zu Fahrtstatus, Fahrerankunft, System-Informationen.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Aufrechterhaltung des App-Betriebs</TableCell>
                <TableCell>alle Nutzer</TableCell>
                <TableCell>Art. 6 Abs. 1 f DSGVO (berechtigtes Interesse)</TableCell>
                <TableCell>Betriebssicherheit, Fehlerdiagnose, Missbrauchsprävention.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Buchhaltungs- & Steuerpflichten</TableCell>
                <TableCell>Lizenznehmer</TableCell>
                <TableCell>Art. 6 Abs. 1 c DSGVO (gesetzliche Pflicht)</TableCell>
                <TableCell>Aufbewahrung von Rechnungs- & Zahlungsdaten nach § 147 AO / § 257 HGB.</TableCell>
              </TableRow>
            </TableBody>
          </TableStyled>
        </TableContainer>

        <SectionTitle variant="h5" component="h2">
          4. Datenkategorien
        </SectionTitle>
        <Typography variant="body1" component="ul">
          <li>Benutzerdaten: UID, Name, Telefonnummer, E-Mail-Adresse, Rolle (Fahrer/Fahrgast), FCM-Token, Profilbild</li>
          <li>Fahrzeugdaten: Fahrzeug-ID, Typ, Kennzeichen, Fahrer-ID, Verfügbarkeitsstatus</li>
          <li>Fahrtdaten: Abhol- & Zieladresse, Datum/Uhrzeit, Preisschätzung, Fahrtstatus, Bewertungsdaten</li>
          <li>Standortdaten: GPS-Koordinaten von Fahrern (kontinuierlich), von Fahrgästen (einmalig zur Abholortbestimmung)</li>
          <li>Schicht- & Verfügbarkeitsdaten (Fahrer)</li>
          <li>Konfigurationsdaten (White-Label-Brand-Farben, Feature-Flags)</li>
        </Typography>

        <SectionTitle variant="h5" component="h2">
          5. Empfänger / Weitergabe der Daten
        </SectionTitle>
        <Typography variant="body1" component="ul">
          <li>Taxiunternehmen (Lizenznehmer): erhalten Fahrgast- und Fahrtdaten zur Durchführung der Beförderung.</li>
          <li>Firebase / Google Ireland Ltd.: Cloud-Hosting, Datenbank, Push-Dienst, Server-Logfiles (Auftragsverarbeiter, SCC gemäß Art. 46 DSGVO).</li>
          <li>Google Maps Platform: Routenberechnung, Geocoding; Anfragen enthalten Standort und Adressdaten.</li>
          <li>Keine Datenweitergabe an Werbenetzwerke oder Social-Media-Dienste.</li>
        </Typography>

        <SectionTitle variant="h5" component="h2">
          6. Datenverarbeitung in Drittländern
        </SectionTitle>
        <Typography variant="body1" paragraph>
          Die Verarbeitung erfolgt ausschließlich in Rechenzentren innerhalb des Europäischen Wirtschaftsraums (EWR). Sollte ausnahmsweise ein Zugriff durch Google-Unternehmen in den USA erfolgen, geschieht dies auf Basis der EU-Standardvertragsklauseln (SCC, 2021/914/EU).
        </Typography>

        <SectionTitle variant="h5" component="h2">
          7. Speicher- & Löschfristen
        </SectionTitle>
        <TableContainer component={Paper} elevation={0} variant="outlined">
          <TableStyled size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Datenkategorie</strong></TableCell>
                <TableCell><strong>Löschfrist</strong></TableCell>
                <TableCell><strong>Mechanismus</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Fahrtdaten (inkl. Standort)</TableCell>
                <TableCell>60 Tage nach Abschluss der Fahrt</TableCell>
                <TableCell>Automatische Cloud-Funktion</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Standort-Live-Daten</TableCell>
                <TableCell>werden mit neuer Position überschrieben</TableCell>
                <TableCell>—</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Fahrgast- & Fahrerprofile</TableCell>
                <TableCell>bis zur Löschung des Accounts oder Widerruf</TableCell>
                <TableCell>manuelle Löschfunktion per E-Mail-Anfrage</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Benachrichtigungen</TableCell>
                <TableCell>12 Monate</TableCell>
                <TableCell>regelmäßige Archivierung</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Rechnungs- & Steuerdaten</TableCell>
                <TableCell>6 / 10 Jahre (AO/HGB)</TableCell>
                <TableCell>gesetzliche Aufbewahrungspflicht</TableCell>
              </TableRow>
            </TableBody>
          </TableStyled>
        </TableContainer>

        <SectionTitle variant="h5" component="h2">
          8. Ihre Rechte
        </SectionTitle>
        <Typography variant="body1" paragraph>
          Sie haben jederzeit das Recht auf Auskunft (Art. 15 DSGVO), Berichtigung (Art. 16), Löschung (Art. 17), Einschränkung (Art. 18), Datenübertragbarkeit (Art. 20) sowie Widerspruch gegen bestimmte Verarbeitungen (Art. 21).
          Anfragen richten Sie bitte an: {brandingConfig.email}
        </Typography>

        <SectionTitle variant="h5" component="h2">
          9. Widerrufsrecht bei Einwilligungen
        </SectionTitle>
        <Typography variant="body1" paragraph>
          Erteilte Einwilligungen (Standort, Push) können Sie jederzeit in den Geräteeinstellungen oder durch Deinstallation der App widerrufen. Die Rechtmäßigkeit der bis zum Widerruf erfolgten Verarbeitung bleibt unberührt.
        </Typography>

        <SectionTitle variant="h5" component="h2">
          10. Pflicht zur Bereitstellung
        </SectionTitle>
        <Typography variant="body1" paragraph>
          Ohne Standort- und Push-Freigabe ist die Nutzung wesentlicher App-Funktionen (Fahrtvermittlung, Echtzeit-Tracking) nicht möglich.
        </Typography>

        <SectionTitle variant="h5" component="h2">
          11. Datensicherheit
        </SectionTitle>
        <Typography variant="body1" paragraph>
          Alle Datenübertragungen erfolgen TLS-verschlüsselt. Daten in Firebase werden verschlüsselt im Ruhezustand gespeichert; Zugriffe sind rollenbasiert limitiert. Regelmäßige Back-ups und Pen-Tests gewährleisten den Schutz vor Datenverlust und unbefugtem Zugriff.
        </Typography>

        <SectionTitle variant="h5" component="h2">
          12. Minderjährige
        </SectionTitle>
        <Typography variant="body1" paragraph>
          Die App richtet sich nicht an Kinder unter 16 Jahren. Personen unter 16 Jahren dürfen die App nur mit Zustimmung der Eltern/Erziehungsberechtigten nutzen.
        </Typography>

        <SectionTitle variant="h5" component="h2">
          13. Beschwerderecht bei der Aufsichtsbehörde
        </SectionTitle>
        <Typography variant="body1" paragraph>
          Sie haben das Recht, sich bei einer Datenschutzaufsichtsbehörde zu beschweren.
          {brandingConfig.dataProtectionAuthority
            ? ` Zuständig ist: ${brandingConfig.dataProtectionAuthority.name}, ${brandingConfig.dataProtectionAuthority.address}.`
            : ' Zuständig ist z. B. das Unabhängige Landeszentrum für Datenschutz Schleswig-Holstein (ULD), Holstenstraße 98, 24103 Kiel, Deutschland.'}
        </Typography>

        <SectionTitle variant="h5" component="h2">
          14. Änderungen dieser Datenschutzerklärung
        </SectionTitle>
        <Typography variant="body1" paragraph>
          Wir passen diese Datenschutzerklärung an, sobald Änderungen der Datenverarbeitung dies erforderlich machen. Die jeweils aktuelle Version ist in der App abrufbar.
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body2" color="textSecondary">
            {brandingConfig.copyright || `© ${currentYear} ${brandingConfig.companyName}`}
          </Typography>
        </Box>
      </PolicyContainer>
    </Container>
  );
};

export default MobilePrivacyPolicy;
